/**
 * Location Catalog Context
 *
 * Owns the full US-state + district catalog for the Compliance Status Lookup
 * page tree. Previously each screen fetched (or received via prop-drilling)
 * its own copy of these two arrays. Centralising here removes the drill and
 * gives every descendant a single source of truth for the "if the login
 * user's assigned scope is empty, fall back to the full catalog" pattern.
 *
 * Scope: mount at the page root that needs it (e.g. ComplianceStatusLookup).
 * Not a Redux slice on purpose — the catalog is cheap to refetch on page
 * (re-)entry and does not need to survive route changes.
 */

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  locationService,
  type State as LocationState,
  type District as LocationDistrict,
} from '@/services/locationService';
import { ensureAllStatesLoaded } from '@/services/manageService';

export interface LocationCatalogState {
  allStates: LocationState[];
  catalogDistricts: LocationDistrict[];
  isLoading: boolean;
}

const LocationCatalogContext = createContext<LocationCatalogState | null>(null);

interface LocationCatalogProviderProps {
  children: React.ReactNode;
}

export function LocationCatalogProvider({ children }: Readonly<LocationCatalogProviderProps>) {
  const [allStates, setAllStates] = useState<LocationState[]>([]);
  const [catalogDistricts, setCatalogDistricts] = useState<LocationDistrict[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    // States use the shared no-op-if-populated helper so we match the
    // caching semantics of the other consumers (Vehicle dialogs) that
    // rely on the same loader.
    ensureAllStatesLoaded(allStates, (states) => {
      if (!cancelled) setAllStates(states);
    });

    locationService
      .fetchAllDistricts(false)
      .then((districts) => {
        if (!cancelled) setCatalogDistricts(districts);
      })
      .catch(() => {
        // Leave the array empty; downstream consumers already tolerate
        // an empty catalog (dropdowns render "no options") and reopening
        // the page will retry.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Fetch once per provider mount. `allStates` is intentionally
    // omitted from deps — ensureAllStatesLoaded is a no-op once populated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoize so descendants relying on referential equality (e.g. downstream
  // useMemo / useEffect dependency arrays) don't rerun on every provider
  // rerender. React S6481.
  const value = useMemo<LocationCatalogState>(
    () => ({ allStates, catalogDistricts, isLoading }),
    [allStates, catalogDistricts, isLoading]
  );

  return (
    <LocationCatalogContext.Provider value={value}>
      {children}
    </LocationCatalogContext.Provider>
  );
}

export function useLocationCatalog(): LocationCatalogState {
  const context = useContext(LocationCatalogContext);
  if (!context) {
    throw new Error('useLocationCatalog must be used within a LocationCatalogProvider');
  }
  return context;
}

/**
 * Non-throwing variant. Returns `null` when called outside a
 * LocationCatalogProvider. Use for optional consumers that may render on
 * pages without the provider mounted.
 */
export function useOptionalLocationCatalog(): LocationCatalogState | null {
  return useContext(LocationCatalogContext);
}
